// src/pages/ItBacklog/ItBacklog.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import ContentWMax from "../../Components/PageLayout/ContentWMax";
import PageTitle from "../../Components/PageTitle/PageTitle";
import styles from "./ItBacklog.module.scss";
import api from "../../services/api";

/**
 * Цели страницы:
 * - Прозрачный бэклог для отделов (канбан/таблица)
 * - Планирование задач по дням на месяц (календарь + матрица загрузки сотрудников)
 * - Задачи: сроки, описание, подзадачи, комментарии
 * - При создании задачи: распределение времени по дням (hoursPerDay) или фиксированные дни
 * - NEW: выбор дней недели, в которые задача выполняется (weekdays)
 * - NEW: редактирование задачи (title/department/priority/owner/description/tags/planning)
 */

const STATUS_META = {
    queue: { label: "В очереди", tone: "queue" },
    in_progress: { label: "В работе", tone: "progress" },
    done: { label: "Готово", tone: "done" },
};

const PRIORITY_META = {
    low: { label: "Низкий", tone: "low" },
    medium: { label: "Средний", tone: "medium" },
    high: { label: "Высокий", tone: "high" },
};

// дни недели для планирования (JS getDay(): 0=Вс ... 6=Сб)
const WEEKDAYS_META = [
    { id: 1, label: "Пн" },
    { id: 2, label: "Вт" },
    { id: 3, label: "Ср" },
    { id: 4, label: "Чт" },
    { id: 5, label: "Пт" },
    { id: 6, label: "Сб" },
    { id: 0, label: "Вс" },
];

/** Утилиты дат */
function pad2(n) {
    return String(n).padStart(2, "0");
}
function toIsoDate(d) {
    const y = d.getFullYear();
    const m = pad2(d.getMonth() + 1);
    const day = pad2(d.getDate());
    return `${y}-${m}-${day}`;
}
function parseIso(iso) {
    const [y, m, d] = String(iso || "").split("-").map(Number);
    if (!y || !m || !d) return null;
    const dt = new Date(y, m - 1, d);
    dt.setHours(0, 0, 0, 0);
    return dt;
}
function formatDate(iso) {
    const dt = parseIso(iso);
    if (!dt) return iso || "—";
    return `${pad2(dt.getDate())}.${pad2(dt.getMonth() + 1)}.${dt.getFullYear()}`;
}
function startOfMonth(dt) {
    return new Date(dt.getFullYear(), dt.getMonth(), 1);
}
function endOfMonth(dt) {
    return new Date(dt.getFullYear(), dt.getMonth() + 1, 0);
}
function eachDayOfMonth(monthDt) {
    const start = startOfMonth(monthDt);
    const end = endOfMonth(monthDt);
    const res = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const nd = new Date(d);
        nd.setHours(0, 0, 0, 0);
        res.push(nd);
    }
    return res;
}
function isWeekend(dt) {
    const wd = dt.getDay();
    return wd === 0 || wd === 6;
}
function getDowShort(dt) {
    const map = ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"];
    return map[dt.getDay()];
}

/** Вспомогательные функции */
function getNameById(list, id) {
    return list.find((x) => x.id === id)?.name || "—";
}
function calcLoadByDept(tasks) {
    const byDept = new Map();
    for (const t of tasks) {
        const cur = byDept.get(t.departmentId) || { total: 0, queue: 0, in_progress: 0, done: 0 };
        cur.total += 1;
        cur[t.status] = (cur[t.status] || 0) + 1;
        byDept.set(t.departmentId, cur);
    }
    return byDept;
}

/**
 * Планирование:
 * - fixedDays: ["YYYY-MM-DD", ...] — задача выполняется только в эти дни
 * - иначе: планируем от startDate..dueDate по hoursPerDay, пока не закроем estimateHours
 * - weekdays: [0..6] — выбранные дни недели (если задано и не пусто — фильтруем по ним)
 * - weekends: по умолчанию пропускаем (можно включить allowWeekends), если weekdays не задан
 */
function buildScheduleForTask(task) {
    const res = new Map(); // iso -> hours
    if (!task) return res;

    const hoursPerDay = Number(task.planning?.hoursPerDay || 0);
    const estimate = Number(task.planning?.estimateHours || 0);
    const allowWeekends = Boolean(task.planning?.allowWeekends);
    const fixedDays = Array.isArray(task.planning?.fixedDays) ? task.planning.fixedDays : [];
    const weekdays = Array.isArray(task.planning?.weekdays) ? task.planning.weekdays : null; // [0..6]

    if (fixedDays.length) {
        const per = hoursPerDay > 0 ? hoursPerDay : fixedDays.length ? estimate / fixedDays.length : 0;
        fixedDays.forEach((iso) => {
            if (!iso) return;
            const hrs = Math.max(0, Number(per || 0));
            if (hrs > 0) res.set(iso, (res.get(iso) || 0) + hrs);
        });
        return res;
    }

    const start = parseIso(task.planning?.startDate);
    const due = parseIso(task.planning?.dueDate);
    if (!start || !due || !hoursPerDay || !estimate) return res;

    let remaining = estimate;
    for (let d = new Date(start); d <= due && remaining > 0; d.setDate(d.getDate() + 1)) {
        const day = new Date(d);
        day.setHours(0, 0, 0, 0);

        if (weekdays && weekdays.length) {
            if (!weekdays.includes(day.getDay())) continue;
        } else {
            if (!allowWeekends && isWeekend(day)) continue;
        }

        const iso = toIsoDate(day);
        const hrs = Math.min(hoursPerDay, remaining);
        if (hrs > 0) res.set(iso, (res.get(iso) || 0) + hrs);
        remaining -= hrs;
    }
    return res;
}

function taskScorePriority(p) {
    return p === "high" ? 3 : p === "medium" ? 2 : 1;
}

function calcOwnerDayLoad(tasks) {
    // ownerId -> isoDay -> { hours, taskIds: [] }
    const map = new Map();
    for (const t of tasks) {
        if (!t.ownerId) continue;
        const sched = buildScheduleForTask(t);
        if (!map.has(t.ownerId)) map.set(t.ownerId, new Map());
        const ownerMap = map.get(t.ownerId);

        for (const [iso, hours] of sched.entries()) {
            const cur = ownerMap.get(iso) || { hours: 0, taskIds: [] };
            cur.hours += hours;
            cur.taskIds.push(t.id);
            ownerMap.set(iso, cur);
        }
    }
    return map;
}

function calcIntensityClass(hours) {
    if (hours <= 0) return styles.cell0;
    if (hours <= 1.5) return styles.cell1;
    if (hours <= 3) return styles.cell2;
    if (hours <= 5) return styles.cell3;
    return styles.cell4;
}

/** API-обертки (используют src/services/api.js) */
async function apiGetDepartments() {
    const { data } = await api.get("/api/it/departments");
    return data?.items || [];
}
async function apiGetOwners() {
    const { data } = await api.get("/api/it/owners");
    return data?.items || [];
}
async function apiGetTasks({ q, status, departmentId, priority }) {
    const { data } = await api.get("/api/it/tasks", {
        params: {
            q: q || undefined,
            status: status && status !== "all" ? status : undefined,
            departmentId: departmentId && departmentId !== "all" ? departmentId : undefined,
            priority: priority && priority !== "all" ? priority : undefined,
        },
    });
    return data?.items || [];
}
async function apiCreateTask(payload) {
    const { data } = await api.post("/api/it/tasks", payload);
    return data?.item;
}
async function apiUpdateTask(taskId, patch) {
    const { data } = await api.patch(`/api/it/tasks/${encodeURIComponent(taskId)}`, patch);
    return data?.item;
}
async function apiAddComment(taskId, { text, authorId }) {
    const { data } = await api.post(`/api/it/tasks/${encodeURIComponent(taskId)}/comments`, { text, authorId });
    return data?.item;
}
async function apiToggleSubtask(taskId, subId) {
    const { data } = await api.patch(`/api/it/tasks/${encodeURIComponent(taskId)}/subtasks/${subId}/toggle`);
    return data?.item;
}

/** Модалка задачи (просмотр + комментарии + редактирование) */
function TaskModal({
                       task,
                       onClose,
                       owners,
                       departments,
                       onAddComment,
                       onToggleSubtask,
                       onUpdateTaskStatus,
                       onUpdateTask,
                   }) {
    const [comment, setComment] = useState("");
    const [busy, setBusy] = useState(false);

    const backdropRef = useRef(null);
    const mouseDownOnBackdrop = useRef(false);

    // edit state
    const [isEdit, setIsEdit] = useState(false);
    const [eTitle, setETitle] = useState("");
    const [eDepartmentId, setEDepartmentId] = useState("");
    const [ePriority, setEPriority] = useState("medium");
    const [eOwnerId, setEOwnerId] = useState("");
    const [eDescription, setEDescription] = useState("");
    const [eTags, setETags] = useState("");

    const [ePlanningMode, setEPlanningMode] = useState("range"); // range | fixed
    const [eStartDate, setEStartDate] = useState(toIsoDate(new Date()));
    const [eDueDate, setEDueDate] = useState(toIsoDate(new Date()));
    const [eEstimateHours, setEEstimateHours] = useState(8);
    const [eHoursPerDay, setEHoursPerDay] = useState(2);
    const [eFixedDays, setEFixedDays] = useState(""); // CSV
    const [eAllowWeekends, setEAllowWeekends] = useState(false);
    const [eWeekdays, setEWeekdays] = useState([1, 2, 3, 4, 5]);

    useEffect(() => {
        if (!task) return;

        // при смене задачи — выходим из режима редактирования и заполняем поля
        setIsEdit(false);
        setComment("");

        setETitle(task.title || "");
        setEDepartmentId(task.departmentId || (departments?.[0]?.id ?? ""));
        setEPriority(task.priority || "medium");
        setEOwnerId(task.ownerId || "");
        setEDescription(task.description || "");
        setETags(Array.isArray(task.tags) ? task.tags.join(", ") : "");

        const p = task.planning || {};
        const fixedDays = Array.isArray(p.fixedDays) ? p.fixedDays : [];
        const weekdays = Array.isArray(p.weekdays) ? p.weekdays : [];

        if (fixedDays.length) {
            setEPlanningMode("fixed");
            setEFixedDays(fixedDays.join(","));
        } else {
            setEPlanningMode("range");
            setEStartDate(p.startDate || toIsoDate(new Date()));
            setEDueDate(p.dueDate || toIsoDate(new Date()));
            setEFixedDays("");
        }

        setEEstimateHours(Number(p.estimateHours || 0) || 8);
        setEHoursPerDay(Number(p.hoursPerDay || 0) || 2);
        setEAllowWeekends(Boolean(p.allowWeekends));
        setEWeekdays(weekdays.length ? weekdays : [1, 2, 3, 4, 5]);
    }, [task, departments]);

    if (!task) return null;

    const statusMeta = STATUS_META[task.status] || STATUS_META.queue;
    const prioMeta = PRIORITY_META[task.priority] || PRIORITY_META.medium;

    const doneCount = (task.subtasks || []).filter((s) => s.done).length;
    const totalSubs = (task.subtasks || []).length;

    const schedule = buildScheduleForTask(task);
    const scheduleItems = Array.from(schedule.entries()).sort(([a], [b]) => a.localeCompare(b));

    const toggleEditWeekday = (id) => {
        setEWeekdays((prev) => {
            const has = prev.includes(id);
            const next = has ? prev.filter((x) => x !== id) : [...prev, id];
            const order = WEEKDAYS_META.map((x) => x.id);
            return next.slice().sort((a, b) => order.indexOf(a) - order.indexOf(b));
        });
    };

    const buildEditPayload = () => ({
        title: eTitle.trim(),
        departmentId: eDepartmentId,
        priority: ePriority,
        ownerId: eOwnerId ? eOwnerId : null,
        description: eDescription.trim(),
        tags: eTags
            .split(",")
            .map((x) => x.trim())
            .filter(Boolean),
        planning:
            ePlanningMode === "fixed"
                ? {
                    fixedDays: eFixedDays
                        .split(",")
                        .map((x) => x.trim())
                        .filter(Boolean),
                    estimateHours: Number(eEstimateHours || 0),
                    hoursPerDay: Number(eHoursPerDay || 0),
                    allowWeekends: Boolean(eAllowWeekends),
                }
                : {
                    startDate: eStartDate,
                    dueDate: eDueDate,
                    estimateHours: Number(eEstimateHours || 0),
                    hoursPerDay: Number(eHoursPerDay || 0),
                    allowWeekends: Boolean(eAllowWeekends),
                    weekdays: Array.isArray(eWeekdays) ? eWeekdays : [],
                },
    });

    return (
        <div
            ref={backdropRef}
            className={styles.modalBackdrop}
            onMouseDown={(e) => {
                mouseDownOnBackdrop.current = e.target === backdropRef.current;
            }}
            onMouseUp={(e) => {
                if (mouseDownOnBackdrop.current && e.target === backdropRef.current) onClose();
                mouseDownOnBackdrop.current = false;
            }}
            role="presentation"
        >
            <div
                className={styles.modal}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseUp={(e) => e.stopPropagation()}
                role="presentation"
            >
                <div className={styles.modalHeader}>
                    <div>
                        <div className={styles.modalTitle}>
                            {task.id}: {isEdit ? "Редактирование" : task.title}
                        </div>
                        <div className={styles.modalSub}>
                            {getNameById(departments, task.departmentId)} · Создано {formatDate(task.createdAt)}
                        </div>
                    </div>

                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        {!isEdit ? (
                            <button type="button" className={styles.actionBtn} onClick={() => setIsEdit(true)} disabled={busy}>
                                Редактировать
                            </button>
                        ) : null}

                        <button type="button" className={styles.modalClose} onClick={onClose} aria-label="Закрыть">
                            ×
                        </button>
                    </div>
                </div>

                <div className={styles.modalBody}>
                    <div className={styles.modalGrid}>
                        <div className={styles.modalCard}>
                            <div className={styles.modalLabel}>Статус</div>
                            <div className={styles.modalValue}>
                                <span className={`${styles.badge} ${styles[statusMeta.tone]}`}>{statusMeta.label}</span>
                            </div>
                            <div className={styles.modalActions}>
                                {task.status !== "queue" ? (
                                    <button
                                        type="button"
                                        className={styles.actionBtn}
                                        disabled={busy}
                                        onClick={async () => {
                                            setBusy(true);
                                            try {
                                                await onUpdateTaskStatus(task.id, "queue");
                                            } finally {
                                                setBusy(false);
                                            }
                                        }}
                                    >
                                        В очередь
                                    </button>
                                ) : null}
                                {task.status !== "in_progress" ? (
                                    <button
                                        type="button"
                                        className={styles.actionBtn}
                                        disabled={busy}
                                        onClick={async () => {
                                            setBusy(true);
                                            try {
                                                await onUpdateTaskStatus(task.id, "in_progress");
                                            } finally {
                                                setBusy(false);
                                            }
                                        }}
                                    >
                                        В работу
                                    </button>
                                ) : null}
                                {task.status !== "done" ? (
                                    <button
                                        type="button"
                                        className={styles.actionBtn}
                                        disabled={busy}
                                        onClick={async () => {
                                            setBusy(true);
                                            try {
                                                await onUpdateTaskStatus(task.id, "done");
                                            } finally {
                                                setBusy(false);
                                            }
                                        }}
                                    >
                                        Готово
                                    </button>
                                ) : null}
                            </div>
                        </div>

                        <div className={styles.modalCard}>
                            <div className={styles.modalLabel}>Приоритет</div>
                            <div className={styles.modalValue}>
                                <span className={`${styles.prio} ${styles[prioMeta.tone]}`}>{prioMeta.label}</span>
                            </div>
                        </div>

                        <div className={styles.modalCard}>
                            <div className={styles.modalLabel}>Исполнитель</div>
                            <div className={styles.modalValue}>{task.ownerId ? getNameById(owners, task.ownerId) : "Не назначен"}</div>
                        </div>

                        <div className={styles.modalCard}>
                            <div className={styles.modalLabel}>Подзадачи</div>
                            <div className={styles.modalValue}>{totalSubs ? `${doneCount}/${totalSubs}` : "—"}</div>
                        </div>
                    </div>

                    {isEdit ? (
                        <>
                            <div className={styles.modalSection}>
                                <div className={styles.modalLabel}>Карточка</div>

                                <div className={styles.formGrid}>
                                    <div className={styles.field}>
                                        <div className={styles.label}>Название</div>
                                        <input className={styles.input} value={eTitle} onChange={(e) => setETitle(e.target.value)} disabled={busy} />
                                    </div>

                                    <div className={styles.field}>
                                        <div className={styles.label}>Отдел</div>
                                        <select className={styles.select} value={eDepartmentId} onChange={(e) => setEDepartmentId(e.target.value)} disabled={busy}>
                                            {departments.map((d) => (
                                                <option key={d.id} value={d.id}>
                                                    {d.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className={styles.field}>
                                        <div className={styles.label}>Приоритет</div>
                                        <select className={styles.select} value={ePriority} onChange={(e) => setEPriority(e.target.value)} disabled={busy}>
                                            <option value="high">Высокий</option>
                                            <option value="medium">Средний</option>
                                            <option value="low">Низкий</option>
                                        </select>
                                    </div>

                                    <div className={styles.field}>
                                        <div className={styles.label}>Исполнитель</div>
                                        <select className={styles.select} value={eOwnerId} onChange={(e) => setEOwnerId(e.target.value)} disabled={busy}>
                                            <option value="">Не назначен</option>
                                            {owners.map((o) => (
                                                <option key={o.id} value={o.id}>
                                                    {o.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className={styles.fieldFull}>
                                        <div className={styles.label}>Описание</div>
                                        <textarea className={styles.textarea} rows={4} value={eDescription} onChange={(e) => setEDescription(e.target.value)} disabled={busy} />
                                    </div>

                                    <div className={styles.fieldFull}>
                                        <div className={styles.label}>Теги (через запятую)</div>
                                        <input className={styles.input} value={eTags} onChange={(e) => setETags(e.target.value)} disabled={busy} />
                                    </div>
                                </div>
                            </div>

                            <div className={styles.modalSection}>
                                <div className={styles.modalLabel}>Планирование</div>

                                <div className={styles.planMode}>
                                    <button
                                        type="button"
                                        className={`${styles.toggleBtn} ${ePlanningMode === "range" ? styles.active : ""}`}
                                        onClick={() => setEPlanningMode("range")}
                                        disabled={busy}
                                    >
                                        Диапазон дат
                                    </button>
                                    <button
                                        type="button"
                                        className={`${styles.toggleBtn} ${ePlanningMode === "fixed" ? styles.active : ""}`}
                                        onClick={() => setEPlanningMode("fixed")}
                                        disabled={busy}
                                    >
                                        Фиксированные дни
                                    </button>
                                </div>

                                {ePlanningMode === "range" ? (
                                    <div className={styles.formGrid}>
                                        <div className={styles.field}>
                                            <div className={styles.label}>Старт</div>
                                            <input className={styles.input} type="date" value={eStartDate} onChange={(e) => setEStartDate(e.target.value)} disabled={busy} />
                                        </div>
                                        <div className={styles.field}>
                                            <div className={styles.label}>Дедлайн</div>
                                            <input className={styles.input} type="date" value={eDueDate} onChange={(e) => setEDueDate(e.target.value)} disabled={busy} />
                                        </div>
                                        <div className={styles.field}>
                                            <div className={styles.label}>Оценка (ч)</div>
                                            <input
                                                className={styles.input}
                                                type="number"
                                                min={1}
                                                value={eEstimateHours}
                                                onChange={(e) => setEEstimateHours(Number(e.target.value))}
                                                disabled={busy}
                                            />
                                        </div>
                                        <div className={styles.field}>
                                            <div className={styles.label}>Часов/день</div>
                                            <input
                                                className={styles.input}
                                                type="number"
                                                min={0.5}
                                                step={0.5}
                                                value={eHoursPerDay}
                                                onChange={(e) => setEHoursPerDay(Number(e.target.value))}
                                                disabled={busy}
                                            />
                                        </div>

                                        <div className={styles.fieldFull}>
                                            <div className={styles.label}>Дни выполнения</div>
                                            <div className={styles.weekdays}>
                                                {WEEKDAYS_META.map((wd) => (
                                                    <label key={wd.id} className={styles.weekdayChip}>
                                                        <input
                                                            type="checkbox"
                                                            checked={eWeekdays.includes(wd.id)}
                                                            onChange={() => toggleEditWeekday(wd.id)}
                                                            disabled={busy}
                                                        />
                                                        <span>{wd.label}</span>
                                                    </label>
                                                ))}
                                            </div>
                                            <div className={styles.smallText}>Задача будет планироваться только в выбранные дни недели.</div>
                                        </div>

                                        <div className={styles.fieldFull}>
                                            <label className={styles.checkRow}>
                                                <input type="checkbox" checked={eAllowWeekends} onChange={(e) => setEAllowWeekends(e.target.checked)} disabled={busy} />
                                                <span>Разрешить выходные (если дни недели не выбраны)</span>
                                            </label>
                                        </div>
                                    </div>
                                ) : (
                                    <div className={styles.formGrid}>
                                        <div className={styles.fieldFull}>
                                            <div className={styles.label}>Дни (CSV: YYYY-MM-DD,YYYY-MM-DD,...)</div>
                                            <input className={styles.input} value={eFixedDays} onChange={(e) => setEFixedDays(e.target.value)} disabled={busy} />
                                        </div>
                                        <div className={styles.field}>
                                            <div className={styles.label}>Оценка (ч)</div>
                                            <input
                                                className={styles.input}
                                                type="number"
                                                min={1}
                                                value={eEstimateHours}
                                                onChange={(e) => setEEstimateHours(Number(e.target.value))}
                                                disabled={busy}
                                            />
                                        </div>
                                        <div className={styles.field}>
                                            <div className={styles.label}>Часов/день (опц.)</div>
                                            <input
                                                className={styles.input}
                                                type="number"
                                                min={0}
                                                step={0.5}
                                                value={eHoursPerDay}
                                                onChange={(e) => setEHoursPerDay(Number(e.target.value))}
                                                disabled={busy}
                                            />
                                        </div>
                                        <div className={styles.fieldFull}>
                                            <label className={styles.checkRow}>
                                                <input type="checkbox" checked={eAllowWeekends} onChange={(e) => setEAllowWeekends(e.target.checked)} disabled={busy} />
                                                <span>Разрешить выходные</span>
                                            </label>
                                        </div>
                                    </div>
                                )}

                                <div className={styles.modalFooter} style={{ marginTop: 12 }}>
                                    <button type="button" className={styles.actionBtn} disabled={busy} onClick={() => setIsEdit(false)}>
                                        Отмена
                                    </button>

                                    <button
                                        type="button"
                                        className={styles.primaryBtn}
                                        disabled={busy}
                                        onClick={async () => {
                                            const t = eTitle.trim();
                                            if (!t) return;

                                            setBusy(true);
                                            try {
                                                await onUpdateTask(task.id, buildEditPayload());
                                                setIsEdit(false);
                                            } finally {
                                                setBusy(false);
                                            }
                                        }}
                                    >
                                        Сохранить
                                    </button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className={styles.modalSection}>
                                <div className={styles.modalLabel}>Описание</div>
                                <div className={styles.modalText}>{task.description || "—"}</div>
                            </div>

                            {task.tags?.length ? (
                                <div className={styles.modalSection}>
                                    <div className={styles.modalLabel}>Теги</div>
                                    <div className={styles.modalTags}>
                                        {task.tags.map((t) => (
                                            <span key={t} className={styles.tag}>
                        {t}
                      </span>
                                        ))}
                                    </div>
                                </div>
                            ) : null}

                            {totalSubs ? (
                                <div className={styles.modalSection}>
                                    <div className={styles.modalLabel}>Чек-лист</div>
                                    <div className={styles.subtasks}>
                                        {task.subtasks.map((s) => (
                                            <label key={String(s.id)} className={styles.subtaskRow}>
                                                <input
                                                    type="checkbox"
                                                    checked={Boolean(s.done)}
                                                    disabled={busy}
                                                    onChange={async () => {
                                                        setBusy(true);
                                                        try {
                                                            await onToggleSubtask(task.id, s.id);
                                                        } finally {
                                                            setBusy(false);
                                                        }
                                                    }}
                                                />
                                                <span className={s.done ? styles.subtaskDone : ""}>{s.title}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            ) : null}

                            <div className={styles.modalSection}>
                                <div className={styles.modalLabel}>План по дням</div>
                                {scheduleItems.length ? (
                                    <div className={styles.scheduleList}>
                                        {scheduleItems.map(([iso, hrs]) => (
                                            <div key={iso} className={styles.scheduleItem}>
                                                <span className={styles.scheduleDate}>{formatDate(iso)}</span>
                                                <span className={styles.scheduleHours}>{hrs.toFixed(1)} ч</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className={styles.mutedBlock}>Планирование не заполнено.</div>
                                )}
                            </div>

                            <div className={styles.modalSection}>
                                <div className={styles.modalLabel}>Комментарии</div>

                                {task.comments?.length ? (
                                    <div className={styles.comments}>
                                        {task.comments
                                            .slice()
                                            .sort((a, b) => String(a.createdAt || "").localeCompare(String(b.createdAt || "")))
                                            .map((c) => (
                                                <div key={String(c.id)} className={styles.commentCard}>
                                                    <div className={styles.commentTop}>
                                                        <div className={styles.commentAuthor}>{getNameById(owners, c.authorId)}</div>
                                                        <div className={styles.commentDate}>{formatDate(c.createdAt)}</div>
                                                    </div>
                                                    <div className={styles.commentText}>{c.text}</div>
                                                </div>
                                            ))}
                                    </div>
                                ) : (
                                    <div className={styles.mutedBlock}>Комментариев пока нет.</div>
                                )}

                                <div className={styles.commentForm}>
                  <textarea
                      className={styles.textarea}
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Добавить комментарий..."
                      rows={3}
                      disabled={busy}
                  />
                                    <button
                                        type="button"
                                        className={styles.primaryBtn}
                                        disabled={busy}
                                        onClick={async () => {
                                            const txt = comment.trim();
                                            if (!txt) return;

                                            setBusy(true);
                                            try {
                                                const authorId = task.ownerId || owners?.[0]?.id || null;
                                                await onAddComment(task.id, { text: txt, authorId });
                                                setComment("");
                                            } finally {
                                                setBusy(false);
                                            }
                                        }}
                                    >
                                        Добавить
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

/** Модалка создания задачи */
function CreateTaskModal({ isOpen, onClose, onCreate, owners, departments }) {
    const [title, setTitle] = useState("");
    const [departmentId, setDepartmentId] = useState(departments[0]?.id || "sales");
    const [priority, setPriority] = useState("medium");
    const [ownerId, setOwnerId] = useState("");
    const [description, setDescription] = useState("");
    const [tags, setTags] = useState("");

    // planning
    const [planningMode, setPlanningMode] = useState("range"); // range | fixed
    const [startDate, setStartDate] = useState(toIsoDate(new Date()));
    const [dueDate, setDueDate] = useState(toIsoDate(new Date()));
    const [estimateHours, setEstimateHours] = useState(8);
    const [hoursPerDay, setHoursPerDay] = useState(2);
    const [fixedDays, setFixedDays] = useState(""); // CSV iso
    const [allowWeekends, setAllowWeekends] = useState(false);

    // weekdays (по умолчанию Пн-Пт)
    const [weekdays, setWeekdays] = useState([1, 2, 3, 4, 5]);

    const backdropRef = useRef(null);
    const mouseDownOnBackdrop = useRef(false);

    useEffect(() => {
        if (!isOpen) return;

        // сброс формы при открытии
        setTitle("");
        setDepartmentId(departments[0]?.id || "sales");
        setPriority("medium");
        setOwnerId("");
        setDescription("");
        setTags("");

        setPlanningMode("range");
        setStartDate(toIsoDate(new Date()));
        setDueDate(toIsoDate(new Date()));
        setEstimateHours(8);
        setHoursPerDay(2);
        setFixedDays("");
        setAllowWeekends(false);

        setWeekdays([1, 2, 3, 4, 5]);
    }, [isOpen, departments]);

    if (!isOpen) return null;

    const toggleWeekday = (id) => {
        setWeekdays((prev) => {
            const has = prev.includes(id);
            const next = has ? prev.filter((x) => x !== id) : [...prev, id];
            const order = WEEKDAYS_META.map((x) => x.id);
            return next.slice().sort((a, b) => order.indexOf(a) - order.indexOf(b));
        });
    };

    return (
        <div
            ref={backdropRef}
            className={styles.modalBackdrop}
            onMouseDown={(e) => {
                mouseDownOnBackdrop.current = e.target === backdropRef.current;
            }}
            onMouseUp={(e) => {
                if (mouseDownOnBackdrop.current && e.target === backdropRef.current) onClose();
                mouseDownOnBackdrop.current = false;
            }}
            role="presentation"
        >
            <div
                className={styles.modal}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseUp={(e) => e.stopPropagation()}
                role="presentation"
            >
                <div className={styles.modalHeader}>
                    <div>
                        <div className={styles.modalTitle}>Создать задачу</div>
                        <div className={styles.modalSub}>Заполните карточку и планирование (по дням).</div>
                    </div>
                    <button type="button" className={styles.modalClose} onClick={onClose} aria-label="Закрыть">
                        ×
                    </button>
                </div>

                <div className={styles.modalBody}>
                    <div className={styles.formGrid}>
                        <div className={styles.field}>
                            <div className={styles.label}>Название</div>
                            <input className={styles.input} value={title} onChange={(e) => setTitle(e.target.value)} />
                        </div>

                        <div className={styles.field}>
                            <div className={styles.label}>Отдел</div>
                            <select className={styles.select} value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
                                {departments.map((d) => (
                                    <option key={d.id} value={d.id}>
                                        {d.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className={styles.field}>
                            <div className={styles.label}>Приоритет</div>
                            <select className={styles.select} value={priority} onChange={(e) => setPriority(e.target.value)}>
                                <option value="high">Высокий</option>
                                <option value="medium">Средний</option>
                                <option value="low">Низкий</option>
                            </select>
                        </div>

                        <div className={styles.field}>
                            <div className={styles.label}>Исполнитель</div>
                            <select className={styles.select} value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
                                <option value="">Не назначен</option>
                                {owners.map((o) => (
                                    <option key={o.id} value={o.id}>
                                        {o.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className={styles.fieldFull}>
                            <div className={styles.label}>Описание</div>
                            <textarea className={styles.textarea} rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
                        </div>

                        <div className={styles.fieldFull}>
                            <div className={styles.label}>Теги (через запятую)</div>
                            <input className={styles.input} value={tags} onChange={(e) => setTags(e.target.value)} />
                        </div>
                    </div>

                    <div className={styles.sectionTitle}>Планирование</div>

                    <div className={styles.planMode}>
                        <button type="button" className={`${styles.toggleBtn} ${planningMode === "range" ? styles.active : ""}`} onClick={() => setPlanningMode("range")}>
                            Диапазон дат
                        </button>
                        <button type="button" className={`${styles.toggleBtn} ${planningMode === "fixed" ? styles.active : ""}`} onClick={() => setPlanningMode("fixed")}>
                            Фиксированные дни
                        </button>
                    </div>

                    {planningMode === "range" ? (
                        <div className={styles.formGrid}>
                            <div className={styles.field}>
                                <div className={styles.label}>Старт</div>
                                <input className={styles.input} type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                            </div>
                            <div className={styles.field}>
                                <div className={styles.label}>Дедлайн</div>
                                <input className={styles.input} type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
                            </div>
                            <div className={styles.field}>
                                <div className={styles.label}>Оценка (ч)</div>
                                <input className={styles.input} type="number" min={1} value={estimateHours} onChange={(e) => setEstimateHours(Number(e.target.value))} />
                            </div>
                            <div className={styles.field}>
                                <div className={styles.label}>Часов/день</div>
                                <input className={styles.input} type="number" min={0.5} step={0.5} value={hoursPerDay} onChange={(e) => setHoursPerDay(Number(e.target.value))} />
                            </div>

                            <div className={styles.fieldFull}>
                                <div className={styles.label}>Дни выполнения</div>
                                <div className={styles.weekdays}>
                                    {WEEKDAYS_META.map((wd) => (
                                        <label key={wd.id} className={styles.weekdayChip}>
                                            <input type="checkbox" checked={weekdays.includes(wd.id)} onChange={() => toggleWeekday(wd.id)} />
                                            <span>{wd.label}</span>
                                        </label>
                                    ))}
                                </div>
                                <div className={styles.smallText}>Задача будет планироваться только в выбранные дни недели.</div>
                            </div>

                            <div className={styles.fieldFull}>
                                <label className={styles.checkRow}>
                                    <input type="checkbox" checked={allowWeekends} onChange={(e) => setAllowWeekends(e.target.checked)} />
                                    <span>Разрешить выходные (если дни недели не выбраны)</span>
                                </label>
                            </div>
                        </div>
                    ) : (
                        <div className={styles.formGrid}>
                            <div className={styles.fieldFull}>
                                <div className={styles.label}>Дни (CSV: YYYY-MM-DD,YYYY-MM-DD,...)</div>
                                <input className={styles.input} value={fixedDays} onChange={(e) => setFixedDays(e.target.value)} />
                            </div>
                            <div className={styles.field}>
                                <div className={styles.label}>Оценка (ч)</div>
                                <input className={styles.input} type="number" min={1} value={estimateHours} onChange={(e) => setEstimateHours(Number(e.target.value))} />
                            </div>
                            <div className={styles.field}>
                                <div className={styles.label}>Часов/день (опц.)</div>
                                <input className={styles.input} type="number" min={0} step={0.5} value={hoursPerDay} onChange={(e) => setHoursPerDay(Number(e.target.value))} />
                            </div>
                        </div>
                    )}

                    <div className={styles.modalFooter}>
                        <button type="button" className={styles.actionBtn} onClick={onClose}>
                            Отмена
                        </button>
                        <button
                            type="button"
                            className={styles.primaryBtn}
                            onClick={() => {
                                const t = title.trim();
                                if (!t) return;

                                const newIdNum = Math.floor(1000 + Math.random() * 9000);
                                const payload = {
                                    id: `T-${newIdNum}`,
                                    title: t,
                                    departmentId,
                                    status: "queue",
                                    priority,
                                    ownerId: ownerId || null,
                                    description: description.trim(),
                                    tags: tags
                                        .split(",")
                                        .map((x) => x.trim())
                                        .filter(Boolean),
                                    planning:
                                        planningMode === "fixed"
                                            ? {
                                                fixedDays: fixedDays
                                                    .split(",")
                                                    .map((x) => x.trim())
                                                    .filter(Boolean),
                                                estimateHours: Number(estimateHours || 0),
                                                hoursPerDay: Number(hoursPerDay || 0),
                                                allowWeekends: Boolean(allowWeekends),
                                            }
                                            : {
                                                startDate,
                                                dueDate,
                                                estimateHours: Number(estimateHours || 0),
                                                hoursPerDay: Number(hoursPerDay || 0),
                                                allowWeekends: Boolean(allowWeekends),
                                                weekdays: Array.isArray(weekdays) ? weekdays : [],
                                            },
                                };

                                onCreate(payload);
                            }}
                        >
                            Создать
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

/** Основной компонент */
export default function ItBacklog() {
    const [departments, setDepartments] = useState([]);
    const [owners, setOwners] = useState([]);
    const [tasks, setTasks] = useState([]);

    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState("");

    // фильтры бэклога
    const [query, setQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("all"); // all | queue | in_progress | done
    const [deptFilter, setDeptFilter] = useState("all");
    const [priorityFilter, setPriorityFilter] = useState("all");

    // режимы
    const [mainTab, setMainTab] = useState("backlog"); // backlog | plan
    const [viewMode, setViewMode] = useState("kanban"); // kanban | table
    const [selectedTask, setSelectedTask] = useState(null);
    const [isCreateOpen, setIsCreateOpen] = useState(false);

    // планирование по месяцу
    const [monthCursor, setMonthCursor] = useState(() => startOfMonth(new Date()));

    useEffect(() => {
        let mounted = true;

        (async () => {
            setLoading(true);
            setErrorMsg("");
            try {
                const [deps, own, t] = await Promise.all([apiGetDepartments(), apiGetOwners(), apiGetTasks({})]);
                if (!mounted) return;
                setDepartments(deps);
                setOwners(own);
                setTasks(t);
            } catch (e) {
                if (!mounted) return;
                setErrorMsg("Не удалось загрузить данные бэклога.");
            } finally {
                if (!mounted) return;
                setLoading(false);
            }
        })();

        return () => {
            mounted = false;
        };
    }, []);

    // если список изменился — синхронизируем selectedTask из tasks
    useEffect(() => {
        if (!selectedTask?.id) return;
        const fresh = tasks.find((t) => t.id === selectedTask.id);
        if (fresh) setSelectedTask(fresh);
    }, [tasks, selectedTask?.id]);

    const deptLoad = useMemo(() => calcLoadByDept(tasks), [tasks]);

    const filteredTasks = useMemo(() => {
        const q = query.trim().toLowerCase();
        return tasks.filter((t) => {
            const okStatus = statusFilter === "all" ? true : t.status === statusFilter;
            const okDept = deptFilter === "all" ? true : t.departmentId === deptFilter;
            const okPrio = priorityFilter === "all" ? true : t.priority === priorityFilter;

            const searchable = `${t.id} ${t.title} ${t.description} ${(t.tags || []).join(" ")}`.toLowerCase();
            const okQuery = q.length === 0 ? true : searchable.includes(q);

            return okStatus && okDept && okPrio && okQuery;
        });
    }, [tasks, query, statusFilter, deptFilter, priorityFilter]);

    const grouped = useMemo(() => {
        const map = { queue: [], in_progress: [], done: [] };
        for (const t of filteredTasks) map[t.status]?.push(t);
        for (const k of Object.keys(map)) {
            map[k].sort((a, b) => {
                const d = taskScorePriority(b.priority) - taskScorePriority(a.priority);
                if (d !== 0) return d;
                return String(a.createdAt || "").localeCompare(String(b.createdAt || ""));
            });
        }
        return map;
    }, [filteredTasks]);

    const total = tasks.length;
    const totalQueue = tasks.filter((t) => t.status === "queue").length;
    const totalInProgress = tasks.filter((t) => t.status === "in_progress").length;

    /** План на месяц: календарь + матрица */
    const monthDays = useMemo(() => eachDayOfMonth(monthCursor), [monthCursor]);
    const ownerDayLoad = useMemo(() => calcOwnerDayLoad(tasks), [tasks]);

    const tasksByDay = useMemo(() => {
        const m = new Map();
        for (const t of tasks) {
            const sched = buildScheduleForTask(t);
            for (const [iso] of sched.entries()) {
                if (!m.has(iso)) m.set(iso, []);
                m.get(iso).push(t);
            }
        }
        for (const [iso, list] of m.entries()) {
            list.sort((a, b) => {
                const sA = a.status === "in_progress" ? 2 : a.status === "queue" ? 1 : 0;
                const sB = b.status === "in_progress" ? 2 : b.status === "queue" ? 1 : 0;
                if (sB !== sA) return sB - sA;

                const p = taskScorePriority(b.priority) - taskScorePriority(a.priority);
                if (p !== 0) return p;

                return String(a.id).localeCompare(String(b.id));
            });
            m.set(iso, list);
        }
        return m;
    }, [tasks]);

    function changeMonth(deltaMonths) {
        setMonthCursor((prev) => new Date(prev.getFullYear(), prev.getMonth() + deltaMonths, 1));
    }

    async function onCreateTask(payload) {
        try {
            const created = await apiCreateTask(payload);
            if (created) setTasks((prev) => [created, ...prev]);
            setIsCreateOpen(false);
        } catch (e) {
            // eslint-disable-next-line no-alert
            alert("Не удалось создать задачу.");
        }
    }

    async function onAddComment(taskId, { text, authorId }) {
        const updated = await apiAddComment(taskId, { text, authorId });
        if (updated) setTasks((prev) => prev.map((t) => (t.id === taskId ? updated : t)));
    }

    async function onToggleSubtask(taskId, subId) {
        const updated = await apiToggleSubtask(taskId, subId);
        if (updated) setTasks((prev) => prev.map((t) => (t.id === taskId ? updated : t)));
    }

    async function onUpdateTaskStatus(taskId, status) {
        const updated = await apiUpdateTask(taskId, { status });
        if (updated) setTasks((prev) => prev.map((t) => (t.id === taskId ? updated : t)));
    }

    // универсальное сохранение изменений задачи из модалки (редактирование)
    async function onUpdateTask(taskId, patch) {
        try {
            const updated = await apiUpdateTask(taskId, patch);
            if (updated) setTasks((prev) => prev.map((t) => (t.id === taskId ? updated : t)));
        } catch (e) {
            // eslint-disable-next-line no-alert
            alert("Не удалось сохранить изменения задачи.");
            throw e;
        }
    }

    return (
        <ContentWMax>
            <PageTitle title="Бэклог IT (прозрачность + планирование)" />

            <div className={styles.page}>
                {loading ? <div className={styles.mutedBlock}>Загрузка...</div> : null}
                {errorMsg ? <div className={styles.mutedBlock}>{errorMsg}</div> : null}

                <div className={styles.topGrid}>
                    <div className={styles.summaryCard}>
                        <div className={styles.summaryTitle}>Сводка</div>

                        <div className={styles.summaryRow}>
                            <div className={styles.summaryItem}>
                                <div className={styles.kpi}>{total}</div>
                                <div className={styles.kpiLabel}>Всего задач</div>
                            </div>
                            <div className={styles.summaryItem}>
                                <div className={styles.kpi}>{totalInProgress}</div>
                                <div className={styles.kpiLabel}>В работе</div>
                            </div>
                            <div className={styles.summaryItem}>
                                <div className={styles.kpi}>{totalQueue}</div>
                                <div className={styles.kpiLabel}>В очереди</div>
                            </div>
                        </div>

                        <div className={styles.legend}>
                            <span className={`${styles.badge} ${styles.progress}`}>В работе</span>
                            <span className={`${styles.badge} ${styles.queue}`}>В очереди</span>
                            <span className={`${styles.badge} ${styles.done}`}>Готово</span>
                        </div>

                        <div className={styles.summaryActions}>
                            <button type="button" className={styles.primaryBtn} onClick={() => setIsCreateOpen(true)} disabled={loading}>
                                + Создать задачу
                            </button>
                        </div>
                    </div>

                    <div className={styles.loadCard}>
                        <div className={styles.loadHeader}>
                            <div className={styles.summaryTitle}>Загрузка по отделам</div>
                            <div className={styles.smallText}>Сколько запросов от каждого отдела сейчас в работе/очереди.</div>
                        </div>

                        <div className={styles.loadList}>
                            {(departments || []).map((d) => {
                                const stat = deptLoad.get(d.id) || { total: 0, queue: 0, in_progress: 0, done: 0 };
                                const inProg = stat.in_progress || 0;
                                const inQueue = stat.queue || 0;
                                const denom = Math.max(1, stat.total);
                                const inProgPct = Math.round((inProg / denom) * 100);
                                const inQueuePct = Math.round((inQueue / denom) * 100);

                                return (
                                    <div key={d.id} className={styles.loadItem}>
                                        <div className={styles.loadLeft}>
                                            <div className={styles.loadDept}>{d.name}</div>
                                            <div className={styles.loadMeta}>
                                                <span className={`${styles.pill} ${styles.progress}`}>В работе: {inProg}</span>
                                                <span className={`${styles.pill} ${styles.queue}`}>В очереди: {inQueue}</span>
                                                <span className={styles.pill}>Всего: {stat.total}</span>
                                            </div>
                                        </div>

                                        <div className={styles.loadRight}>
                                            <div className={styles.bars}>
                                                <div className={styles.barTrack}>
                                                    <div className={`${styles.barFill} ${styles.progress}`} style={{ width: `${inProgPct}%` }} />
                                                    <div className={`${styles.barFill} ${styles.queue}`} style={{ width: `${inQueuePct}%` }} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                            {!departments?.length && !loading ? <div className={styles.mutedBlock}>Нет отделов.</div> : null}
                        </div>
                    </div>
                </div>

                <div className={styles.tabs}>
                    <button type="button" className={`${styles.tabBtn} ${mainTab === "backlog" ? styles.tabActive : ""}`} onClick={() => setMainTab("backlog")}>
                        Бэклог
                    </button>
                    <button type="button" className={`${styles.tabBtn} ${mainTab === "plan" ? styles.tabActive : ""}`} onClick={() => setMainTab("plan")}>
                        План на месяц
                    </button>
                </div>

                {mainTab === "backlog" ? (
                    <>
                        <div className={styles.toolbar}>
                            <div className={styles.searchWrap}>
                                <input className={styles.search} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Поиск: ID, название, теги, описание..." />
                            </div>

                            <div className={styles.filters}>
                                <select className={styles.select} value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)}>
                                    <option value="all">Все отделы</option>
                                    {departments.map((d) => (
                                        <option key={d.id} value={d.id}>
                                            {d.name}
                                        </option>
                                    ))}
                                </select>

                                <select className={styles.select} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                    <option value="all">Все статусы</option>
                                    <option value="in_progress">В работе</option>
                                    <option value="queue">В очереди</option>
                                    <option value="done">Готово</option>
                                </select>

                                <select className={styles.select} value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
                                    <option value="all">Любой приоритет</option>
                                    <option value="high">Высокий</option>
                                    <option value="medium">Средний</option>
                                    <option value="low">Низкий</option>
                                </select>

                                <div className={styles.viewToggle}>
                                    <button type="button" className={`${styles.toggleBtn} ${viewMode === "kanban" ? styles.active : ""}`} onClick={() => setViewMode("kanban")}>
                                        Канбан
                                    </button>
                                    <button type="button" className={`${styles.toggleBtn} ${viewMode === "table" ? styles.active : ""}`} onClick={() => setViewMode("table")}>
                                        Таблица
                                    </button>
                                </div>
                            </div>
                        </div>

                        {viewMode === "kanban" ? (
                            <div className={styles.kanban}>
                                {(["in_progress", "queue"] /* done можно включить */).map((col) => (
                                    <div key={col} className={styles.column}>
                                        <div className={styles.columnHeader}>
                                            <div className={styles.columnTitle}>
                                                <span className={`${styles.badge} ${styles[STATUS_META[col].tone]}`}>{STATUS_META[col].label}</span>
                                            </div>
                                            <div className={styles.columnCount}>{grouped[col].length}</div>
                                        </div>

                                        <div className={styles.cards}>
                                            {grouped[col].map((t) => (
                                                <button key={t.id} type="button" className={styles.taskCard} onClick={() => setSelectedTask(t)}>
                                                    <div className={styles.taskTop}>
                                                        <div className={styles.taskId}>{t.id}</div>
                                                        <span className={`${styles.prio} ${styles[PRIORITY_META[t.priority].tone]}`}>{PRIORITY_META[t.priority].label}</span>
                                                    </div>

                                                    <div className={styles.taskTitle}>{t.title}</div>

                                                    <div className={styles.taskMetaRow}>
                                                        <div className={styles.taskMeta}>
                                                            <span className={styles.metaLabel}>Отдел:</span> {getNameById(departments, t.departmentId)}
                                                        </div>
                                                        <div className={styles.taskMeta}>
                                                            <span className={styles.metaLabel}>Исполнитель:</span> {t.ownerId ? getNameById(owners, t.ownerId) : "Не назначен"}
                                                        </div>
                                                    </div>

                                                    <div className={styles.taskBottom}>
                                                        <div className={styles.smallText}>Создано: {formatDate(t.createdAt)}</div>
                                                        <div className={styles.smallText}>
                                                            План:{" "}
                                                            {(() => {
                                                                const s = buildScheduleForTask(t);
                                                                const totalH = Array.from(s.values()).reduce((a, b) => a + b, 0);
                                                                return totalH ? `${totalH.toFixed(0)} ч` : "—";
                                                            })()}
                                                        </div>
                                                    </div>

                                                    {t.tags?.length ? (
                                                        <div className={styles.tags}>
                                                            {t.tags.slice(0, 3).map((tag) => (
                                                                <span key={tag} className={styles.tag}>
                                  {tag}
                                </span>
                                                            ))}
                                                            {t.tags.length > 3 ? <span className={styles.tag}>+{t.tags.length - 3}</span> : null}
                                                        </div>
                                                    ) : null}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className={styles.tableWrap}>
                                <table className={styles.table}>
                                    <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Задача</th>
                                        <th>Отдел</th>
                                        <th>Статус</th>
                                        <th>Приоритет</th>
                                        <th>Исполнитель</th>
                                        <th>Создано</th>
                                        <th>План (ч)</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {filteredTasks.map((t) => {
                                        const s = buildScheduleForTask(t);
                                        const totalH = Array.from(s.values()).reduce((a, b) => a + b, 0);
                                        return (
                                            <tr key={t.id} onClick={() => setSelectedTask(t)} className={styles.rowClickable}>
                                                <td className={styles.mono}>{t.id}</td>
                                                <td>{t.title}</td>
                                                <td>{getNameById(departments, t.departmentId)}</td>
                                                <td>
                                                    <span className={`${styles.badge} ${styles[STATUS_META[t.status].tone]}`}>{STATUS_META[t.status].label}</span>
                                                </td>
                                                <td>
                                                    <span className={`${styles.prio} ${styles[PRIORITY_META[t.priority].tone]}`}>{PRIORITY_META[t.priority].label}</span>
                                                </td>
                                                <td>{t.ownerId ? getNameById(owners, t.ownerId) : "Не назначен"}</td>
                                                <td>{formatDate(t.createdAt)}</td>
                                                <td>{totalH ? `${totalH.toFixed(1)} ч` : "—"}</td>
                                            </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        <div className={styles.planToolbar}>
                            <div className={styles.monthNav}>
                                <button type="button" className={styles.actionBtn} onClick={() => changeMonth(-1)}>
                                    ←
                                </button>
                                <div className={styles.monthTitle}>{monthCursor.toLocaleDateString("ru-RU", { month: "long", year: "numeric" })}</div>
                                <button type="button" className={styles.actionBtn} onClick={() => changeMonth(1)}>
                                    →
                                </button>
                            </div>

                            <div className={styles.planHint}>
                                <div className={styles.smallText}>Матрица: строки — сотрудники, столбцы — дни. Ячейка показывает суммарные часы и список задач.</div>
                            </div>
                        </div>

                        <div className={styles.planGrid}>
                            <div className={styles.calendarCard}>
                                <div className={styles.summaryTitle}>Календарь задач (по дням)</div>

                                <div className={styles.calendar}>
                                    {monthDays.map((d) => {
                                        const iso = toIsoDate(d);
                                        const list = tasksByDay.get(iso) || [];
                                        return (
                                            <div key={iso} className={`${styles.dayCell} ${isWeekend(d) ? styles.weekend : ""}`}>
                                                <div className={styles.dayTop}>
                                                    <div className={styles.dayNum}>{d.getDate()}</div>
                                                    <div className={styles.dayDow}>{getDowShort(d)}</div>
                                                </div>

                                                <div className={styles.dayTasks}>
                                                    {list.slice(0, 3).map((t) => {
                                                        const sched = buildScheduleForTask(t);
                                                        const hrs = sched.get(iso) || 0;
                                                        return (
                                                            <button
                                                                key={`${iso}_${t.id}`}
                                                                type="button"
                                                                className={styles.dayTaskChip}
                                                                onClick={() => setSelectedTask(t)}
                                                                title={`${t.id} · ${t.title}`}
                                                            >
                                                                <span className={`${styles.dot} ${styles[STATUS_META[t.status].tone]}`} />
                                                                <span className={styles.chipText}>
                                  {t.id} · {hrs ? `${hrs.toFixed(1)}ч` : ""}
                                </span>
                                                            </button>
                                                        );
                                                    })}
                                                    {list.length > 3 ? <div className={styles.more}>+{list.length - 3} еще</div> : null}
                                                    {!list.length ? <div className={styles.dayEmpty}>—</div> : null}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className={styles.matrixCard}>
                                <div className={styles.summaryTitle}>Загрузка сотрудников (ч/день)</div>

                                <div className={styles.matrixWrap}>
                                    <table className={styles.matrix}>
                                        <thead>
                                        <tr>
                                            <th className={styles.stickyCol}>Сотрудник</th>
                                            {monthDays.map((d) => (
                                                <th key={toIsoDate(d)} className={isWeekend(d) ? styles.weekendHead : ""}>
                                                    <div className={styles.headDay}>{d.getDate()}</div>
                                                    <div className={styles.headDow}>{getDowShort(d)}</div>
                                                </th>
                                            ))}
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {owners.map((o) => {
                                            const rowMap = ownerDayLoad.get(o.id) || new Map();
                                            return (
                                                <tr key={o.id}>
                                                    <td className={`${styles.stickyCol} ${styles.ownerCell}`}>{o.name}</td>
                                                    {monthDays.map((d) => {
                                                        const iso = toIsoDate(d);
                                                        const cell = rowMap.get(iso);
                                                        const hours = cell?.hours || 0;
                                                        const taskIds = cell?.taskIds || [];

                                                        const cls = calcIntensityClass(hours);
                                                        const shown = taskIds.slice(0, 2).join(", ");
                                                        const more = taskIds.length > 2 ? ` +${taskIds.length - 2}` : "";

                                                        return (
                                                            <td
                                                                key={`${o.id}_${iso}`}
                                                                className={`${styles.matrixCell} ${cls} ${isWeekend(d) ? styles.weekendCell : ""}`}
                                                                title={taskIds.length ? `${o.name} · ${formatDate(iso)} · ${hours.toFixed(1)}ч · ${taskIds.join(", ")}` : ""}
                                                            >
                                                                <div className={styles.cellHours}>{hours ? hours.toFixed(1) : ""}</div>
                                                                <div className={styles.cellTasks}>{shown ? `${shown}${more}` : ""}</div>
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            );
                                        })}
                                        </tbody>
                                    </table>
                                </div>

                                <div className={styles.matrixLegend}>
                                    <span className={`${styles.legendBox} ${styles.cell0}`} />
                                    <span className={styles.smallText}>0</span>
                                    <span className={`${styles.legendBox} ${styles.cell1}`} />
                                    <span className={styles.smallText}>до 1.5ч</span>
                                    <span className={`${styles.legendBox} ${styles.cell2}`} />
                                    <span className={styles.smallText}>до 3ч</span>
                                    <span className={`${styles.legendBox} ${styles.cell3}`} />
                                    <span className={styles.smallText}>до 5ч</span>
                                    <span className={`${styles.legendBox} ${styles.cell4}`} />
                                    <span className={styles.smallText}>5ч+</span>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                <CreateTaskModal
                    isOpen={isCreateOpen}
                    onClose={() => setIsCreateOpen(false)}
                    onCreate={onCreateTask}
                    owners={owners}
                    departments={departments}
                />

                <TaskModal
                    task={selectedTask}
                    onClose={() => setSelectedTask(null)}
                    owners={owners}
                    departments={departments}
                    onAddComment={onAddComment}
                    onToggleSubtask={onToggleSubtask}
                    onUpdateTaskStatus={onUpdateTaskStatus}
                    onUpdateTask={onUpdateTask}
                />
            </div>
        </ContentWMax>
    );
}
